import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, CheckCircle, Warning, CalendarBlank, Users, Plus, PencilSimple, Trash, SignIn, SignOut, Backspace, FileXls, Printer, Envelope, FunnelSimple, Storefront } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { AanwezigheidFiliaalTabel } from "@/components/AanwezigheidFiliaalTabel";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth, usePermissions } from "@/App";
import api from "@/lib/api";
import { openDownload } from "@/lib/downloadUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Aanwezigheid() {
  const user = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "medewerker";
  const isMedewerker = role === "medewerker";
  const [records, setRecords] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [datum, setDatum] = useState(() => new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editUren, setEditUren] = useState("");
  const [editIn, setEditIn] = useState("");
  const [editUit, setEditUit] = useState("");
  const [urenHandmatig, setUrenHandmatig] = useState(false);
  const [form, setForm] = useState({ medewerker_id: "", filiaal_id: "", datum: "", uren: "" });
  const [manualMode, setManualMode] = useState("dag"); // "dag" | "bulk" | "rooster"
  const [bulkForm, setBulkForm] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    const first = today.slice(0, 7) + "-01";
    return {
      medewerker_id: "",
      filiaal_id: "",
      datum_van: first,
      datum_tot: today,
      weekdagen: [0, 1, 2, 3, 4], // Ma-Vr standaard
      uren: "8",
      overschrijven: false,
    };
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  // Maandrooster state
  const now = new Date();
  const [roosterMaand, setRoosterMaand] = useState(String(now.getMonth() + 1));
  const [roosterJaar, setRoosterJaar] = useState(String(now.getFullYear()));
  const [roosterMedewerker, setRoosterMedewerker] = useState("");
  // dagen: { "YYYY-MM-DD": [{filiaal_id, uren}, {filiaal_id, uren}] }
  const [roosterDagen, setRoosterDagen] = useState({});
  const [roosterSaving, setRoosterSaving] = useState(false);
  const ROOSTER_SLOTS = 3; // max aantal filialen per dag

  // Snel inklokken
  const [snelMedewerker, setSnelMedewerker] = useState("");
  const [snelFiliaal, setSnelFiliaal] = useState(() => localStorage.getItem("aanwezigheid_filiaal") || "");
  const [snelProcessing, setSnelProcessing] = useState(false);
  const [snelPin, setSnelPin] = useState("");
  const [snelIdentified, setSnelIdentified] = useState(null);
  const [snelPinChecked, setSnelPinChecked] = useState(""); // laatste PIN die daadwerkelijk gechecked is

  // Overzicht filters + email dialog
  const [filterFiliaal, setFilterFiliaal] = useState("");
  const [filterMedewerker, setFilterMedewerker] = useState("");
  const [filterTeLaat, setFilterTeLaat] = useState(false);
  const [rangeMode, setRangeMode] = useState(false);
  const [datumVan, setDatumVan] = useState(() => new Date().toISOString().split("T")[0]);
  const [datumTot, setDatumTot] = useState(() => new Date().toISOString().split("T")[0]);
  const [rangeRecords, setRangeRecords] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recentEmails, setRecentEmails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("recent_report_emails") || "[]"); }
    catch { return []; }
  });

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const [res, filRes, medRes] = await Promise.all([
        axios.get(`${API}/aanwezigheid`, { params: { datum } }),
        axios.get(`${API}/filialen`),
        axios.get(`${API}/medewerkers`),
      ]);
      setRecords(res.data);
      setFilialen(filRes.data);
      setMedewerkers(medRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [datum]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Eén filiaal in de firma? Dan automatisch kiezen (bij meerdere zelf selecteren)
  useEffect(() => {
    if (filialen.length === 1 && snelFiliaal !== filialen[0].id) {
      setSnelFiliaal(filialen[0].id);
      localStorage.setItem("aanwezigheid_filiaal", filialen[0].id);
    }
  }, [filialen, snelFiliaal]);

  // Auto-select filiaal when medewerker changes
  useEffect(() => {
    if (snelMedewerker) {
      const m = medewerkers.find((med) => med.id === snelMedewerker);
      if (m && m.filiaal_ids?.length > 0 && !snelFiliaal) setSnelFiliaal(m.filiaal_ids[0]);
    }
  }, [snelMedewerker, medewerkers, snelFiliaal]);

  // Voor medewerker rol: alleen eigen rij tonen in dropdowns
  const visibleMedewerkers = isMedewerker
    ? medewerkers.filter((m) => m.id === user?.id)
    : medewerkers;

  // Filiaal als voorkeur: medewerkers van dat filiaal bovenaan, de rest eronder
  const snelFiliaalMedewerkers = snelFiliaal
    ? visibleMedewerkers.filter((m) => (m.filiaal_ids || []).includes(snelFiliaal))
    : visibleMedewerkers;
  const snelAndereMedewerkers = snelFiliaal
    ? visibleMedewerkers.filter((m) => !(m.filiaal_ids || []).includes(snelFiliaal))
    : [];

  const kiesSnelFiliaal = (v) => {
    setSnelFiliaal(v);
    localStorage.setItem("aanwezigheid_filiaal", v);
  };

  // Auto-select eigen naam bij medewerker rol
  useEffect(() => {
    if (isMedewerker && user?.id && !snelMedewerker) {
      setSnelMedewerker(user.id);
    }
  }, [isMedewerker, user?.id, snelMedewerker]);

  // PIN lookup (met debounce — zoekt pas zodra er 4+ cijfers zijn en wacht tot je stopt met typen)
  useEffect(() => {
    if (snelPin.length < 4) {
      setSnelIdentified(null);
      setSnelPinChecked("");
      return;
    }
    const t = setTimeout(() => {
      axios.post(`${API}/pin/lookup`, { pincode: snelPin }).then((res) => {
        setSnelIdentified(res.data);
        setSnelPinChecked(snelPin);
        if (res.data.filiaal_ids?.length > 0 && !snelFiliaal) setSnelFiliaal(res.data.filiaal_ids[0]);
      }).catch(() => {
        setSnelIdentified(null);
        setSnelPinChecked(snelPin);
      });
    }, 500);
    return () => clearTimeout(t);
  }, [snelPin, snelFiliaal]);

  // Admin/manager met 'klokken_anderen': inklokken zonder pincode
  const perms = usePermissions();
  const canKlokAnderen = perms[role]?.klokken_anderen === true;

  const geselecteerdIngeklokt = snelMedewerker
    ? records.find((r) => r.medewerker_id === snelMedewerker && !r.uitklok_tijd && !r.handmatig)
    : null;

  const handleAdminInklokken = async () => {
    if (!snelMedewerker || !snelFiliaal) return;
    setSnelProcessing(true);
    try {
      await api.post("/inklokken", { medewerker_id: snelMedewerker, filiaal_id: snelFiliaal, pincode: "" });
      toast.success("Ingeklokt!");
      setSnelPin("");
      fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Inklokken mislukt"); }
    finally { setSnelProcessing(false); }
  };

  const handleAdminUitklokken = async () => {
    if (!snelMedewerker) return;
    setSnelProcessing(true);
    try {
      await api.post("/uitklokken", { medewerker_id: snelMedewerker, pincode: "" });
      toast.success("Uitgeklokt!");
      setSnelPin("");
      fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Uitklokken mislukt"); }
    finally { setSnelProcessing(false); }
  };

  const [uitklokBusy, setUitklokBusy] = useState(null);
  const [uitklokSelectie, setUitklokSelectie] = useState([]);
  const [bulkUitklokBusy, setBulkUitklokBusy] = useState(false);

  const toggleUitklokSelectie = (id) =>
    setUitklokSelectie((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkUitklokken = async (lijst) => {
    if (lijst.length === 0) return;
    setBulkUitklokBusy(true);
    let gelukt = 0;
    for (const r of lijst) {
      try {
        await api.post("/uitklokken", { medewerker_id: r.medewerker_id, pincode: "" });
        gelukt += 1;
      } catch (e) {
        toast.error(`${r.medewerker_naam}: ${e.response?.data?.detail || "uitklokken mislukt"}`);
      }
    }
    setUitklokSelectie([]);
    setBulkUitklokBusy(false);
    if (gelukt) toast.success(`${gelukt} medewerker${gelukt === 1 ? "" : "s"} uitgeklokt`);
    fetchRecords();
  };

  const uitklokMedewerker = async (record) => {
    setUitklokBusy(record.medewerker_id);
    try {
      await api.post("/uitklokken", { medewerker_id: record.medewerker_id, pincode: "" });
      toast.success(`${record.medewerker_naam} uitgeklokt`);
      fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Uitklokken mislukt"); }
    finally { setUitklokBusy(null); }
  };

  const isSnelIngeklokt = snelIdentified
    ? records.find((r) => r.medewerker_id === snelIdentified.medewerker_id && !r.uitklok_tijd && !r.handmatig)
    : null;

  const handleSnelPinInklokken = async () => {
    if (!snelPin || !snelFiliaal || !snelIdentified) return;
    setSnelProcessing(true);
    try {
      await axios.post(`${API}/pin/inklokken`, { pincode: snelPin, filiaal_id: snelFiliaal });
      toast.success("Ingeklokt!");
      setSnelPin(""); setSnelIdentified(null);
      fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setSnelProcessing(false); }
  };

  const handleSnelPinUitklokken = async () => {
    if (!snelPin || !snelIdentified) return;
    setSnelProcessing(true);
    try {
      await axios.post(`${API}/pin/uitklokken`, { pincode: snelPin });
      toast.success("Uitgeklokt!");
      setSnelPin(""); setSnelIdentified(null);
      fetchRecords();
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setSnelProcessing(false); }
  };

  const formatTijd = (isoStr) => {
    if (!isoStr) return "-";
    return new Date(isoStr).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  };

  const openManualEntry = () => {
    setManualMode("dag");
    setForm({ medewerker_id: "", filiaal_id: "", datum: datum, uren: "" });
    setBulkForm((b) => ({ ...b, medewerker_id: "", filiaal_id: "", uren: "8" }));
    setDialogOpen(true);
  };

  const handleBulkSave = async () => {
    const b = bulkForm;
    if (!b.medewerker_id || !b.filiaal_id || !b.datum_van || !b.datum_tot || !b.uren) {
      toast.error("Vul alle velden in");
      return;
    }
    if (b.weekdagen.length === 0) {
      toast.error("Selecteer minstens één weekdag");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/aanwezigheid/handmatig/bulk`, {
        medewerker_id: b.medewerker_id,
        filiaal_id: b.filiaal_id,
        datum_van: b.datum_van,
        datum_tot: b.datum_tot,
        weekdagen: b.weekdagen,
        uren: parseFloat(b.uren),
        overschrijven: b.overschrijven,
      });
      const d = res.data || {};
      const parts = [];
      if (d.aangemaakt) parts.push(`${d.aangemaakt} nieuw`);
      if (d.overschreven) parts.push(`${d.overschreven} bijgewerkt`);
      if (d.overgeslagen_bestaat)
        parts.push(`${d.overgeslagen_bestaat} overgeslagen (bestond al)`);
      toast.success(parts.length ? parts.join(" · ") : "Bulk voltooid");
      setDialogOpen(false);
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij bulk-invoer");
    } finally {
      setBulkSaving(false);
    }
  };

  const toggleWeekdag = (idx) => {
    setBulkForm((b) => {
      const has = b.weekdagen.includes(idx);
      return {
        ...b,
        weekdagen: has ? b.weekdagen.filter((x) => x !== idx) : [...b.weekdagen, idx].sort(),
      };
    });
  };

  // ── Maandrooster helpers ──
  const getDagenVanMaand = (jaar, maand) => {
    const y = parseInt(jaar);
    const m = parseInt(maand);
    const last = new Date(y, m, 0).getDate();
    const arr = [];
    for (let d = 1; d <= last; d++) {
      const ds = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const wd = new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
      // naar 0=Ma..6=Zo
      const weekdag = (wd + 6) % 7;
      arr.push({ datum: ds, dag: d, weekdag });
    }
    return arr;
  };

  const MAAND_NAMEN_DROP = [
    "Januari", "Februari", "Maart", "April", "Mei", "Juni",
    "Juli", "Augustus", "September", "Oktober", "November", "December",
  ];
  const DAGNAMEN_KORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

  // Laad bestaande uren voor geselecteerde medewerker + maand en vul rooster
  const laadRooster = async () => {
    if (!roosterMedewerker) {
      toast.error("Selecteer eerst een medewerker");
      return;
    }
    const jaar = parseInt(roosterJaar);
    const maand = parseInt(roosterMaand);
    const ds_van = `${jaar}-${String(maand).padStart(2, "0")}-01`;
    const last = new Date(jaar, maand, 0).getDate();
    const ds_tot = `${jaar}-${String(maand).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    try {
      const res = await axios.get(`${API}/aanwezigheid`, {
        params: { datum_van: ds_van, datum_tot: ds_tot },
      });
      const mine = (res.data || []).filter(
        (r) => r.medewerker_id === roosterMedewerker
      );
      const map = {};
      mine.forEach((r) => {
        const key = r.datum;
        if (!map[key]) map[key] = [];
        if (map[key].length < ROOSTER_SLOTS) {
          map[key].push({
            filiaal_id: r.filiaal_id || "",
            uren: r.uren ? String(r.uren) : "",
          });
        }
      });
      setRoosterDagen(map);
      const aantal = Object.values(map).reduce((s, v) => s + v.length, 0);
      toast.success(`${aantal} bestaande record${aantal === 1 ? "" : "s"} geladen`);
    } catch (e) {
      toast.error("Fout bij laden");
    }
  };

  const setRoosterSlot = (datum, slotIdx, field, value) => {
    setRoosterDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      while (cur.length <= slotIdx) cur.push({ filiaal_id: "", uren: "" });
      cur[slotIdx] = { ...cur[slotIdx], [field]: value };
      return { ...prev, [datum]: cur };
    });
  };

  const addRoosterSlot = (datum) => {
    setRoosterDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      if (cur.length >= ROOSTER_SLOTS) return prev;
      cur.push({ filiaal_id: "", uren: "" });
      return { ...prev, [datum]: cur };
    });
  };

  const removeRoosterSlot = (datum, slotIdx) => {
    setRoosterDagen((prev) => {
      const cur = prev[datum] ? [...prev[datum]] : [];
      cur.splice(slotIdx, 1);
      const next = { ...prev };
      if (cur.length === 0) delete next[datum];
      else next[datum] = cur;
      return next;
    });
  };

  const vulAllesIn = (uren, filiaalId, alleenWerkdagen = true) => {
    const dagen = getDagenVanMaand(roosterJaar, roosterMaand);
    setRoosterDagen((prev) => {
      const next = { ...prev };
      dagen.forEach(({ datum, weekdag }) => {
        if (alleenWerkdagen && weekdag >= 5) return; // skip weekend
        next[datum] = [{ filiaal_id: filiaalId, uren: String(uren) }];
      });
      return next;
    });
    toast.success(alleenWerkdagen ? "Ma-Vr ingevuld" : "Alle dagen ingevuld");
  };

  const handleRoosterSave = async () => {
    if (!roosterMedewerker) {
      toast.error("Selecteer een medewerker");
      return;
    }
    const entries = [];
    Object.entries(roosterDagen).forEach(([datum, slots]) => {
      slots.forEach((s) => {
        const u = parseFloat(s.uren);
        if (s.filiaal_id && isFinite(u) && u > 0) {
          entries.push({ datum, filiaal_id: s.filiaal_id, uren: u });
        }
      });
    });
    if (entries.length === 0) {
      toast.error("Geen dagen ingevuld");
      return;
    }
    setRoosterSaving(true);
    try {
      const res = await axios.post(`${API}/aanwezigheid/handmatig/rooster`, {
        medewerker_id: roosterMedewerker,
        entries,
        overschrijven: true,
      });
      const d = res.data || {};
      const parts = [];
      if (d.aangemaakt) parts.push(`${d.aangemaakt} nieuw`);
      if (d.overschreven) parts.push(`${d.overschreven} bijgewerkt`);
      toast.success(parts.length ? parts.join(" · ") : "Opgeslagen");
      setDialogOpen(false);
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    } finally {
      setRoosterSaving(false);
    }
  };

  const handleManualSave = async () => {
    if (!form.medewerker_id || !form.filiaal_id || !form.datum || !form.uren) {
      toast.error("Vul alle velden in");
      return;
    }
    try {
      await axios.post(`${API}/aanwezigheid/handmatig`, {
        medewerker_id: form.medewerker_id,
        filiaal_id: form.filiaal_id,
        datum: form.datum,
        uren: parseFloat(form.uren),
      });
      toast.success("Uren handmatig toegevoegd");
      setDialogOpen(false);
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij toevoegen");
    }
  };

  // ISO-tijd -> "HH:MM" in Belgische tijd
  const naarTijdVeld = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("nl-BE", {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Brussels",
    });
  };

  const openEdit = (r) => {
    setEditRecord(r);
    setEditUren(String(r.uren || 0));
    setEditIn(naarTijdVeld(r.inklok_tijd));
    setEditUit(naarTijdVeld(r.uitklok_tijd));
    setUrenHandmatig(false);
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editRecord) return;
    if (editIn && editUit && editUit <= editIn) {
      toast.error("Uitkloktijd moet na de inkloktijd liggen");
      return;
    }
    try {
      const body = { inklok_tijd: editIn, uitklok_tijd: editUit };
      // Uren enkel meesturen als je ze zelf hebt aangepast, anders rekent de server ze uit
      if (urenHandmatig || (!editIn && !editUit)) body.uren = parseFloat(editUren) || 0;
      const r = await axios.put(`${API}/aanwezigheid/${editRecord.id}`, body);
      toast.success(`Bijgewerkt — ${(r.data?.uren ?? 0).toFixed(2).replace(".", ",")}u`);
      setEditDialog(false);
      fetchRecords();
      if (rangeMode) fetchRangeRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij bijwerken");
    }
  };

  const fetchRangeRecords = async () => {
    if (!rangeMode) return;
    setRangeLoading(true);
    try {
      const r = await axios.get(`${API}/aanwezigheid`, { params: { datum_van: datumVan, datum_tot: datumTot } });
      setRangeRecords(r.data || []);
    } catch {
      setRangeRecords([]);
    } finally {
      setRangeLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/aanwezigheid/${id}`);
      // Optimistic update: verwijder direct uit beide lijsten zodat de UI meteen reageert
      setRecords((prev) => prev.filter((r) => r.id !== id));
      setRangeRecords((prev) => prev.filter((r) => r.id !== id));
      toast.success("Record verwijderd");
      // Volledige refresh voor zekerheid
      fetchRecords();
      if (rangeMode) fetchRangeRecords();
    } catch (e) {
      toast.error("Fout bij verwijderen");
    }
  };

  const totaalAanwezig = records.filter((r) => !r.uitklok_tijd && !r.handmatig).length;
  const totaalTeLaat = records.filter((r) => r.te_laat).length;
  const totaalRegistraties = records.length;
  const nogIngeklokt = totaalAanwezig;

  // Range records fetchen wanneer range mode aan staat
  useEffect(() => {
    fetchRangeRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeMode, datumVan, datumTot]);

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().split("T")[0];
    if (preset === "today") {
      setRangeMode(false);
      setDatum(iso(today));
    } else if (preset === "week") {
      const day = today.getDay() || 7;
      const monday = new Date(today); monday.setDate(today.getDate() - day + 1);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      setRangeMode(true);
      setDatumVan(iso(monday)); setDatumTot(iso(sunday));
    } else if (preset === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setRangeMode(true);
      setDatumVan(iso(start)); setDatumTot(iso(end));
    } else if (preset === "last7") {
      const start = new Date(today); start.setDate(today.getDate() - 6);
      setRangeMode(true);
      setDatumVan(iso(start)); setDatumTot(iso(today));
    }
  };

  // Filter records voor overzicht-tab (gebruik range-records als range actief)
  const sourceRecords = rangeMode ? rangeRecords : records;
  const filteredRecords = sourceRecords.filter((r) => {
    if (filterFiliaal && r.filiaal_id !== filterFiliaal) return false;
    if (filterMedewerker && r.medewerker_id !== filterMedewerker) return false;
    if (filterTeLaat && !r.te_laat) return false;
    return true;
  });

  // Groepeer per dag → per filiaal (inklapbaar overzicht)
  const dagGroepen = useMemo(() => {
    const perDag = new Map();
    filteredRecords.forEach((r) => {
      const d = r.datum || datum;
      if (!perDag.has(d)) perDag.set(d, new Map());
      const fmap = perDag.get(d);
      const fnaam = r.filiaal_naam || "Onbekend filiaal";
      if (!fmap.has(fnaam)) fmap.set(fnaam, []);
      fmap.get(fnaam).push(r);
    });
    return [...perDag.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([dag, fmap]) => {
        const filialenLijst = [...fmap.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([naam, recs]) => ({
            naam,
            records: recs,
            uren: recs.reduce((s, r) => s + (r.uren || 0), 0),
            medewerkers: new Set(recs.map((r) => r.medewerker_id)).size,
            teLaat: recs.filter((r) => r.te_laat).length,
          }));
        const alle = filialenLijst.flatMap((f) => f.records);
        // Pauze = gat tussen uitklokken en opnieuw inklokken op dezelfde dag
        const pauzes = {};
        const perMedewerker = new Map();
        alle.forEach((r) => {
          if (!perMedewerker.has(r.medewerker_id)) perMedewerker.set(r.medewerker_id, []);
          perMedewerker.get(r.medewerker_id).push(r);
        });
        perMedewerker.forEach((recs) => {
          if (recs.length < 2) return;
          const sorted = [...recs].sort((a, b) => (a.inklok_tijd || "").localeCompare(b.inklok_tijd || ""));
          for (let i = 1; i < sorted.length; i += 1) {
            const vorige = sorted[i - 1];
            const huidig = sorted[i];
            if (!vorige.uitklok_tijd || !huidig.inklok_tijd) continue;
            const gat = (new Date(huidig.inklok_tijd) - new Date(vorige.uitklok_tijd)) / 60000;
            if (gat > 0 && gat < 600) pauzes[huidig.id] = Math.round(gat);
          }
        });
        return {
          dag,
          filialen: filialenLijst,
          pauzes,
          uren: alle.reduce((s, r) => s + (r.uren || 0), 0),
          medewerkers: new Set(alle.map((r) => r.medewerker_id)).size,
          registraties: alle.length,
          teLaat: alle.filter((r) => r.te_laat).length,
        };
      });
  }, [filteredRecords, datum]);

  const exportParams = () => {
    const params = new URLSearchParams();
    if (rangeMode) {
      params.set("datum_van", datumVan);
      params.set("datum_tot", datumTot);
    } else {
      params.set("datum", datum);
    }
    if (filterFiliaal) params.set("filiaal_id", filterFiliaal);
    if (filterMedewerker) params.set("medewerker_id", filterMedewerker);
    if (filterTeLaat) params.set("alleen_te_laat", "true");
    return params.toString();
  };

  const exportExcel = () => openDownload(`${API}/export/aanwezigheid-excel?${exportParams()}`);

  const printOverzicht = () => openDownload(`${API}/export/aanwezigheid-pdf?${exportParams()}`);

  const dagLabel = (d) => {
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long" });
  };

  const sendEmail = async () => {
    if (!emailTo) { toast.error("Vul een email adres in"); return; }
    setSendingEmail(true);
    try {
      const payload = {
        email: emailTo,
        filiaal_id: filterFiliaal,
        medewerker_id: filterMedewerker,
        alleen_te_laat: filterTeLaat,
      };
      if (rangeMode) { payload.datum_van = datumVan; payload.datum_tot = datumTot; }
      else { payload.datum = datum; }
      await axios.post(`${API}/email/aanwezigheid`, payload);
      toast.success(`Aanwezigheid verstuurd naar ${emailTo}`);
      const next = [emailTo, ...recentEmails.filter((e) => e !== emailTo)].slice(0, 5);
      setRecentEmails(next);
      localStorage.setItem("recent_report_emails", JSON.stringify(next));
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij versturen");
    } finally { setSendingEmail(false); }
  };

  const hasActiveFilters = filterFiliaal || filterMedewerker || filterTeLaat;

  return (
    <div data-testid="aanwezigheid-page" className="space-y-6">
      {!isMedewerker && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="page-title">Aanwezigheid</h1>
              <p className="text-sm text-muted-foreground mt-1">Registratie van in- en uitkloktijden</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <CalendarBlank size={18} className="text-muted-foreground shrink-0" />
              <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="rounded-sm flex-1 sm:w-44" data-testid="datum-filter" />
              <Button onClick={() => navigate("/uren-invoer")} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="add-manual-btn">
                <Plus size={16} weight="bold" className="mr-1" /> Handmatig
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="stat-card flex items-center gap-3" data-testid="stat-aanwezig">
              <div className="hidden sm:flex w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center"><Users size={20} weight="bold" className="text-[#002FA7]" /></div>
              <div><p className="section-label">Aanwezig</p><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{totaalAanwezig}</p></div>
            </div>
            <div className="stat-card flex items-center gap-3" data-testid="stat-te-laat">
              <div className="hidden sm:flex w-9 h-9 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center"><Warning size={20} weight="bold" className="text-[#E53E3E]" /></div>
              <div><p className="section-label">Te Laat</p><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{totaalTeLaat}</p></div>
            </div>
            <div className="stat-card flex items-center gap-3" data-testid="stat-nog-ingeklokt">
              <div className="hidden sm:flex w-9 h-9 rounded-sm bg-[#38A169]/10 flex items-center justify-center"><CheckCircle size={20} weight="bold" className="text-[#38A169]" /></div>
              <div><p className="section-label">Totaal Vandaag</p><p className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{totaalRegistraties}</p></div>
            </div>
          </div>
        </>
      )}

      {/* Tabs: Inklokken | Overzicht */}
      <Tabs defaultValue="inklokken" className="w-full">
        <TabsList className="rounded-sm w-full grid grid-cols-2 h-auto sm:inline-flex sm:w-auto" data-testid="aanwezigheid-tabs">
          <TabsTrigger value="inklokken" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-inklokken">
            <SignIn size={14} weight="bold" className="mr-1.5" /> Inklokken
          </TabsTrigger>
          <TabsTrigger value="overzicht" className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-overzicht">
            <Clock size={14} weight="bold" className="mr-1.5" /> Overzicht {records.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-white text-[#002FA7] rounded-sm border border-[#002FA7]/20">{records.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inklokken" className="mt-5">
          {/* Snel Inklokken met PIN numpad */}
          <div className="bg-white border border-[#002FA7]/20 rounded-sm overflow-hidden" data-testid="snel-inklokken">
        <div className="bg-[#002FA7] text-white px-4 py-2.5 flex items-center justify-between">
          <span className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Inklokken / Uitklokken</span>
          <span className="text-lg font-black tabular-nums">{new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div className="p-4 max-w-md mx-auto">
          {/* Filiaal & Medewerker */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs font-semibold">Filiaal</Label>
              <Select value={snelFiliaal} onValueChange={kiesSnelFiliaal}>
                <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="snel-filiaal"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                <SelectContent>{filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Medewerker</Label>
              <Select value={snelMedewerker} onValueChange={(v) => { setSnelMedewerker(v); setSnelPin(""); setSnelIdentified(null); }}>
                <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="snel-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                <SelectContent>
                  {snelFiliaalMedewerkers.length === 0 && snelAndereMedewerkers.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">Geen medewerkers</div>
                  ) : (
                    <>
                      <SelectGroup>
                        {snelFiliaal && (
                          <SelectLabel className="text-[10px] uppercase tracking-wider">
                            {filialen.find((f) => f.id === snelFiliaal)?.naam || "Filiaal"}
                          </SelectLabel>
                        )}
                        {snelFiliaalMedewerkers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                        ))}
                      </SelectGroup>
                      {snelAndereMedewerkers.length > 0 && (
                        <>
                          <SelectSeparator />
                          <SelectGroup>
                            <SelectLabel className="text-[10px] uppercase tracking-wider">Andere medewerkers</SelectLabel>
                            {snelAndereMedewerkers.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                            ))}
                          </SelectGroup>
                        </>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* PIN dots */}
          {!canKlokAnderen && <div className="flex justify-center gap-3 mb-3" data-testid="snel-pin-dots">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${i < snelPin.length ? "bg-[#002FA7] border-[#002FA7] scale-125" : "bg-transparent border-gray-300"}`} />
            ))}
          </div>}

          {/* Identified */}
          {snelIdentified && (
            <div className="bg-[#002FA7] text-white rounded-sm p-3 mb-3 flex items-center gap-3" data-testid="snel-identified">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold text-base">
                {snelIdentified.voornaam?.[0]}{snelIdentified.achternaam?.[0]}
              </div>
              <span className="font-bold">{snelIdentified.naam}</span>
              {isSnelIngeklokt && <span className="ml-auto text-xs font-bold bg-[#38A169] px-2 py-0.5 rounded-sm">INGEKLOKT</span>}
            </div>
          )}
          {snelPin.length === 6 && !snelIdentified && snelPinChecked === snelPin && (
            <div className="bg-[#E53E3E] text-white rounded-sm p-2 mb-3 text-center text-sm font-bold">Ongeldige pincode</div>
          )}

          {/* Numpad — admin/manager heeft geen pincode nodig */}
          {!canKlokAnderen && <div className="grid grid-cols-3 gap-2 mb-3" data-testid="snel-numpad">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button key={n} onClick={() => { if (snelPin.length < 6) setSnelPin(p => p + n); }} className="h-[60px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-[#002FA7] active:text-white text-xl font-bold transition-colors select-none" data-testid={`snel-numpad-${n}`}>{n}</button>
            ))}
            <button onClick={() => { setSnelPin(""); setSnelIdentified(null); }} className="h-[60px] rounded-sm bg-gray-100 hover:bg-gray-200 text-sm font-bold select-none" data-testid="snel-numpad-clear">Wis</button>
            <button onClick={() => { if (snelPin.length < 6) setSnelPin(p => p + "0"); }} className="h-[60px] rounded-sm bg-gray-100 hover:bg-gray-200 active:bg-[#002FA7] active:text-white text-xl font-bold transition-colors select-none" data-testid="snel-numpad-0">0</button>
            <button onClick={() => setSnelPin(p => p.slice(0, -1))} className="h-[60px] rounded-sm bg-gray-100 hover:bg-gray-200 flex items-center justify-center select-none" data-testid="snel-numpad-back"><Backspace size={24} weight="bold" /></button>
          </div>}

            {/* Dynamische actieknop: inklokken ALS nog niet ingeklokt, anders uitklokken */}
            <div className="grid grid-cols-1 gap-3">
              {canKlokAnderen ? (
                geselecteerdIngeklokt ? (
                  <button
                    onClick={handleAdminUitklokken}
                    disabled={snelProcessing || !snelMedewerker}
                    className="h-16 rounded-sm bg-[#E53E3E] hover:bg-[#C53030] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 select-none transition-colors"
                    data-testid="admin-uitklokken-btn"
                  >
                    <SignOut size={22} weight="bold" /> UITKLOKKEN
                  </button>
                ) : (
                  <button
                    onClick={handleAdminInklokken}
                    disabled={snelProcessing || !snelMedewerker || !snelFiliaal}
                    className="h-16 rounded-sm bg-[#38A169] hover:bg-[#2F855A] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 select-none transition-colors"
                    data-testid="admin-inklokken-btn"
                  >
                    <SignIn size={22} weight="bold" /> INKLOKKEN
                  </button>
                )
              ) : isSnelIngeklokt ? (
                <button
                  onClick={handleSnelPinUitklokken}
                  disabled={snelProcessing || !snelIdentified}
                  className="h-16 rounded-sm bg-[#E53E3E] hover:bg-[#C53030] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 select-none transition-colors"
                  data-testid="snel-uitklokken-btn"
                >
                  <SignOut size={22} weight="bold" /> UITKLOKKEN
                </button>
              ) : (
                <button
                  onClick={handleSnelPinInklokken}
                  disabled={snelProcessing || !snelIdentified || !snelFiliaal}
                  className="h-16 rounded-sm bg-[#38A169] hover:bg-[#2F855A] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 select-none transition-colors"
                  data-testid="snel-inklokken-btn"
                >
                  <SignIn size={22} weight="bold" /> INKLOKKEN
                </button>
              )}
              {((canKlokAnderen && snelMedewerker && !snelFiliaal && !geselecteerdIngeklokt) || (!canKlokAnderen && snelIdentified && !snelFiliaal && !isSnelIngeklokt)) && (
                <div className="text-[11px] text-amber-700 text-center font-semibold">
                  ⚠ Selecteer eerst een filiaal
                </div>
              )}
              {canKlokAnderen && (
                <p className="text-[11px] text-muted-foreground text-center">
                  Als beheerder klok je in/uit zonder pincode — kies enkel medewerker en filiaal.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Nu ingeklokt in dit filiaal — rechtstreeks uitklokken */}
        {(() => {
          const ingeklokt = records
            .filter((r) => !r.uitklok_tijd && !r.handmatig)
            .filter((r) => (snelFiliaal ? r.filiaal_id === snelFiliaal : true));
          const filiaalNaam = filialen.find((f) => f.id === snelFiliaal)?.naam;
          return (
            <div className="bg-white border border-border rounded-sm overflow-hidden mt-4" data-testid="ingeklokt-lijst">
              <div className="px-3 sm:px-4 py-2.5 bg-muted">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                    Nu ingeklokt {snelFiliaal ? `— ${filiaalNaam}` : "— alle filialen"}
                  </span>
                  <span className="text-xs font-black tabular-nums text-[#002FA7] ml-auto shrink-0" data-testid="ingeklokt-aantal">
                    {ingeklokt.length}
                  </span>
                </div>
                {canKlokAnderen && ingeklokt.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                    <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer shrink-0">
                      <Checkbox
                        className="shrink-0 h-5 w-5"
                        checked={uitklokSelectie.length === ingeklokt.length}
                        onCheckedChange={(v) =>
                          setUitklokSelectie(v ? ingeklokt.map((r) => r.medewerker_id) : [])
                        }
                        data-testid="selecteer-alles-checkbox"
                      />
                      Alles selecteren
                    </label>
                    <div className="flex gap-2 sm:ml-auto">
                      <Button
                        onClick={() => bulkUitklokken(ingeklokt.filter((r) => uitklokSelectie.includes(r.medewerker_id)))}
                        disabled={bulkUitklokBusy || uitklokSelectie.length === 0}
                        size="sm"
                        className="rounded-sm h-9 text-[11px] px-2.5 flex-1 sm:flex-none bg-[#E53E3E] hover:bg-[#C53030] text-white"
                        data-testid="uitklok-selectie-btn"
                      >
                        Selectie ({uitklokSelectie.length})
                      </Button>
                      <Button
                        onClick={() => bulkUitklokken(ingeklokt)}
                        disabled={bulkUitklokBusy}
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-9 text-[11px] px-2.5 flex-1 sm:flex-none border-[#E53E3E] text-[#E53E3E] hover:bg-[#E53E3E] hover:text-white"
                        data-testid="uitklok-alle-btn"
                      >
                        Allemaal
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {ingeklokt.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="ingeklokt-leeg">
                  Niemand ingeklokt{snelFiliaal ? " in dit filiaal" : ""}
                </p>
              ) : (
                ingeklokt.map((r) => (
                  <div key={r.id} className="px-3 sm:px-4 py-2.5 border-t border-border flex items-center gap-2.5 sm:gap-3" data-testid={`ingeklokt-rij-${r.medewerker_id}`}>
                    {canKlokAnderen && (
                      <Checkbox
                        className="shrink-0 h-5 w-5"
                        checked={uitklokSelectie.includes(r.medewerker_id)}
                        onCheckedChange={() => toggleUitklokSelectie(r.medewerker_id)}
                        data-testid={`uitklok-check-${r.medewerker_id}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{r.medewerker_naam}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        <span className="font-mono">{formatTijd(r.inklok_tijd)}</span>
                        {!snelFiliaal && r.filiaal_naam ? ` · ${r.filiaal_naam}` : ""}
                        {r.te_laat ? " · te laat" : ""}
                      </p>
                    </div>
                    {canKlokAnderen && (
                      <Button
                        onClick={() => uitklokMedewerker(r)}
                        disabled={uitklokBusy === r.medewerker_id}
                        size="sm"
                        className="rounded-sm h-9 w-10 sm:w-auto px-0 sm:px-3 bg-[#E53E3E] hover:bg-[#C53030] text-white shrink-0"
                        data-testid={`uitklok-btn-${r.medewerker_id}`}
                      >
                        <SignOut size={15} weight="bold" className="sm:mr-1.5" />
                        <span className="hidden sm:inline">{uitklokBusy === r.medewerker_id ? "..." : "Uitklokken"}</span>
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          );
        })()}
        </TabsContent>

        <TabsContent value="overzicht" className="mt-5 space-y-3" data-testid="overzicht-tab">
          {/* Datum-range controles */}
          <div className="bg-white border border-border rounded-sm p-3 flex flex-wrap items-end gap-3 print:hidden" data-testid="date-range-toolbar">
            <div className="flex items-center gap-1 mr-2">
              {[
                { k: "today", l: "Vandaag" },
                { k: "week", l: "Deze week" },
                { k: "last7", l: "Laatste 7 d" },
                { k: "month", l: "Deze maand" },
              ].map((p) => (
                <button
                  key={p.k}
                  onClick={() => applyPreset(p.k)}
                  className="text-[11px] font-semibold px-2 py-1.5 rounded-sm border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors"
                  data-testid={`preset-${p.k}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="range-toggle">
                <input type="checkbox" checked={rangeMode} onChange={(e) => setRangeMode(e.target.checked)} className="accent-[#002FA7]" />
                <span className="font-semibold">Periode</span>
              </label>
              {rangeMode ? (
                <>
                  <Input type="date" value={datumVan} onChange={(e) => setDatumVan(e.target.value)} className="rounded-sm w-40 h-8" data-testid="range-van" />
                  <span className="text-muted-foreground text-xs">tot</span>
                  <Input type="date" value={datumTot} onChange={(e) => setDatumTot(e.target.value)} className="rounded-sm w-40 h-8" data-testid="range-tot" />
                </>
              ) : (
                <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="rounded-sm w-40 h-8" data-testid="range-single" />
              )}
            </div>
          </div>

          {/* Filter + Acties balk */}
          <div className="bg-white border border-border rounded-sm p-3 flex flex-wrap items-end gap-3 print:hidden" data-testid="overzicht-toolbar">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Filiaal</Label>
              <Select value={filterFiliaal || "all"} onValueChange={(v) => setFilterFiliaal(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="filter-filiaal"><SelectValue placeholder="Alle filialen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle filialen</SelectItem>
                  {filialen.map((f) => <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Medewerker</Label>
              <Select value={filterMedewerker || "all"} onValueChange={(v) => setFilterMedewerker(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="filter-medewerker"><SelectValue placeholder="Alle medewerkers" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Alle medewerkers</SelectItem>
                  {visibleMedewerkers.map((m) => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground block">Status</Label>
              <label className="flex items-center gap-1.5 h-8 px-2 border border-border rounded-sm cursor-pointer hover:bg-muted/50 text-sm" data-testid="filter-telaat">
                <input type="checkbox" checked={filterTeLaat} onChange={(e) => setFilterTeLaat(e.target.checked)} className="accent-[#E53E3E]" />
                Alleen te laat
              </label>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => { setFilterFiliaal(""); setFilterMedewerker(""); setFilterTeLaat(false); }}
                className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground underline self-end"
                data-testid="clear-filters"
              >
                Wis filters
              </button>
            )}
            <div className="ml-auto flex gap-2 self-end">
              <Button onClick={exportExcel} variant="outline" size="sm" className="rounded-sm" data-testid="aan-export-excel"><FileXls size={14} weight="bold" className="mr-1" /> Excel</Button>
              <Button onClick={printOverzicht} variant="outline" size="sm" className="rounded-sm" data-testid="aan-print"><Printer size={14} weight="bold" className="mr-1" /> Print / PDF</Button>
              <Button onClick={() => setEmailOpen(true)} size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="aan-email"><Envelope size={14} weight="bold" className="mr-1" /> Email</Button>
            </div>
          </div>

          {(loading || rangeLoading) ? (
            <div className="h-48 bg-muted animate-pulse rounded-sm" />
          ) : filteredRecords.length === 0 ? (
            <div className="bg-white border border-border rounded-sm p-12 text-center text-muted-foreground" data-testid="no-aanwezigheid">
              <FunnelSimple size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                {hasActiveFilters ? "Geen registraties die aan de filters voldoen" : "Geen aanwezigheidsregistraties voor deze periode"}
              </p>
            </div>
          ) : (
            <>
            {/* Overzicht per dag → per filiaal (altijd open) */}
            <div className="space-y-2 print:hidden" data-testid="aanwezigheid-accordeon">
              {dagGroepen.map((g) => {
                return (
                  <div key={g.dag} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`dag-groep-${g.dag}`}>
                    <div
                      className="w-full flex items-center gap-2 px-3 py-3 text-left"
                      data-testid={`dag-kop-${g.dag}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-black capitalize truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
                          {dagLabel(g.dag)}
                        </p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {g.medewerkers} medewerker{g.medewerkers === 1 ? "" : "s"} · {String(g.uren.toFixed(2)).replace(".", ",")} u
                          {g.teLaat > 0 ? ` · ${g.teLaat}x te laat` : ""}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {g.filialen.map((f) => (
                            <span key={f.naam} className="text-[10px] font-semibold bg-[#EAF1FF] text-[#002FA7] rounded-full px-2 py-0.5">
                              {f.naam} · {f.medewerkers}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {(
                      <div className="border-t border-border" data-testid={`dag-inhoud-${g.dag}`}>
                        {g.filialen.map((f) => {
                          const key = `${g.dag}|${f.naam}`;
                          return (
                            <div key={key} className="border-b border-border last:border-b-0">
                              <div
                                className="w-full flex items-center gap-2 px-3 py-2 bg-muted/40 text-left"
                                data-testid={`filiaal-kop-${key}`}
                              >
                                <Storefront size={14} weight="bold" className="text-[#002FA7] shrink-0" />
                                <span className="text-xs font-bold uppercase tracking-wider truncate flex-1">{f.naam}</span>
                                <span className="text-[11px] font-bold tabular-nums text-muted-foreground shrink-0">
                                  {f.medewerkers} · {String(f.uren.toFixed(2)).replace(".", ",")} u
                                </span>
                              </div>

                              {(
                                <AanwezigheidFiliaalTabel
                                  filiaal={f}
                                  pauzes={g.pauzes}
                                  onEdit={openEdit}
                                  onDelete={handleDelete}
                                />
                              )}
                            </div>
                          );
                        })}
                        <div
                          className="flex items-center gap-3 px-3 py-2.5 border-t-2 border-[#002FA7]/30 bg-[#002FA7]/5 text-xs font-bold"
                          data-testid={`dag-totaal-${g.dag}`}
                        >
                          <span className="uppercase tracking-wider text-[#002FA7]">Dagtotaal</span>
                          <span className="text-muted-foreground font-semibold">
                            {g.medewerkers} medewerker{g.medewerkers === 1 ? "" : "s"} · {g.registraties} registratie{g.registraties === 1 ? "" : "s"}
                            {g.teLaat > 0 ? ` · ${g.teLaat}x te laat` : ""}
                          </span>
                          <span className="ml-auto tabular-nums text-base font-black">
                            {String(g.uren.toFixed(2)).replace(".", ",")} u
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden print:block bg-white border border-border rounded-sm overflow-x-auto print:border-black" data-testid="aanwezigheid-table" id="aanwezigheid-print">
              <div className="hidden print:block px-4 py-3 border-b border-black">
                <div className="font-black text-lg">Aanwezigheid {rangeMode ? `${datumVan} t/m ${datumTot}` : datum}</div>
                <div className="text-xs text-muted-foreground">
                  {filteredRecords.length} registraties · totaal uren {filteredRecords.reduce((s, r) => s + (r.uren || 0), 0).toFixed(2)}
                  {filterFiliaal && ` · filiaal: ${filialen.find((f) => f.id === filterFiliaal)?.naam || ""}`}
                  {filterMedewerker && ` · medewerker: ${medewerkers.find((m) => m.id === filterMedewerker)?.voornaam || ""} ${medewerkers.find((m) => m.id === filterMedewerker)?.achternaam || ""}`}
                  {filterTeLaat && " · alleen te laat"}
                </div>
              </div>
              <table className="data-table [&_th]:border [&_td]:border [&_th]:border-[#999] [&_td]:border-[#999] print:[&_th]:border-black print:[&_td]:border-black">
                <thead>
                  <tr>
                    {rangeMode && <th>Datum</th>}
                    <th>Medewerker</th>
                    <th>Filiaal</th>
                    <th>In</th>
                    <th>Uit</th>
                    <th>Duur</th>
                    <th>Pauze</th>
                    <th>Te laat</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dagGroepen.map((g) => (
                    <Fragment key={g.dag}>
                      {g.filialen.map((f) => (
                        <Fragment key={`${g.dag}|${f.naam}`}>
                          {f.records.map((r) => (
                            <tr key={r.id} data-testid={`aanwezigheid-row-${r.id}`}>
                              {rangeMode && <td className="font-mono text-xs text-muted-foreground">{r.datum}</td>}
                              <td className="font-medium">{r.medewerker_naam}</td>
                              <td>{r.filiaal_naam}</td>
                              <td className="font-mono text-sm">{r.handmatig ? "-" : formatTijd(r.inklok_tijd)}</td>
                              <td className="font-mono text-sm">{r.handmatig ? "-" : formatTijd(r.uitklok_tijd)}</td>
                              <td className="font-semibold tabular-nums">{r.uren ? String(r.uren).replace(".", ",") : "-"}</td>
                              <td className="tabular-nums text-sm">{g.pauzes[r.id] ? `${g.pauzes[r.id]} min` : "-"}</td>
                              <td className="text-sm">{r.te_laat ? "Ja" : ""}</td>
                              <td className="text-sm">
                                {r.handmatig ? "Handmatig" : !r.uitklok_tijd ? "Actief" : "Voltooid"}
                              </td>
                            </tr>
                          ))}
                          <tr className="font-bold bg-muted/40" data-testid={`print-subtotaal-${g.dag}-${f.naam}`}>
                            <td colSpan={rangeMode ? 5 : 4}>
                              Subtotaal {f.naam} · {f.medewerkers} medewerker{f.medewerkers === 1 ? "" : "s"}
                              {f.teLaat > 0 ? ` · ${f.teLaat}x te laat` : ""}
                            </td>
                            <td className="tabular-nums">{String(f.uren.toFixed(2)).replace(".", ",")}</td>
                            <td colSpan={3} />
                          </tr>
                        </Fragment>
                      ))}
                      <tr className="font-black border-t-2 border-black" data-testid={`print-dagtotaal-${g.dag}`}>
                        <td colSpan={rangeMode ? 5 : 4}>
                          Dagtotaal {dagLabel(g.dag)} · {g.medewerkers} medewerker{g.medewerkers === 1 ? "" : "s"}
                        </td>
                        <td className="tabular-nums">{String(g.uren.toFixed(2)).replace(".", ",")}</td>
                        <td colSpan={3} />
                      </tr>
                    </Fragment>
                  ))}
                  <tr className="font-black border-t-2 border-black">
                    <td colSpan={rangeMode ? 5 : 4}>
                      TOTAAL · {new Set(filteredRecords.map((r) => r.medewerker_id)).size} medewerker(s) · {filteredRecords.length} registratie(s)
                    </td>
                    <td className="tabular-nums">
                      {filteredRecords.reduce((s, r) => s + (r.uren || 0), 0).toFixed(2).replace(".", ",")}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Email Aanwezigheid Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="aan-email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Aanwezigheid Versturen</DialogTitle>
            <DialogDescription>
              Verstuur aanwezigheid van <b>{rangeMode ? `${datumVan} t/m ${datumTot}` : datum}</b> per email met Excel bijlage.
              {hasActiveFilters && " De huidige filters worden toegepast."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold">Email Adres</Label>
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="collega@gontex.be" className="rounded-sm mt-1" data-testid="aan-email-input" />
            {recentEmails.length > 0 && (
              <div className="mt-3">
                <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Recent gebruikt</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {recentEmails.map((addr) => (
                    <button
                      key={addr}
                      type="button"
                      onClick={() => setEmailTo(addr)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-border hover:border-[#002FA7] bg-muted/40 text-xs hover:text-[#002FA7]"
                      data-testid={`aan-pick-email-${addr}`}
                    >
                      {addr}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="aan-send-email-btn">
              <Envelope size={14} weight="bold" className="mr-1" /> {sendingEmail ? "Versturen..." : "Versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className={`rounded-sm ${
            manualMode === "rooster" ? "sm:max-w-5xl max-h-[90vh] overflow-y-auto" : "sm:max-w-md"
          }`}
          data-testid="manual-entry-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Handmatige Uren Invoer</DialogTitle>
            <DialogDescription>
              {manualMode === "dag"
                ? "Voeg handmatig uren toe voor 1 dag (bijv. vergeten uit te klokken)"
                : manualMode === "bulk"
                ? "Vul uren in voor meerdere dagen tegelijk (hele maand of periode)"
                : "Volledig maandrooster: kies filiaal + uren per dag (meerdere filialen mogelijk)"}
            </DialogDescription>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="grid grid-cols-3 gap-1 p-1 bg-muted rounded-sm" data-testid="manual-mode-toggle">
            <button
              onClick={() => setManualMode("dag")}
              className={`text-xs font-bold uppercase tracking-wider py-2 rounded-sm transition-colors ${
                manualMode === "dag" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
              }`}
              data-testid="manual-mode-dag"
            >
              1 Dag
            </button>
            <button
              onClick={() => setManualMode("bulk")}
              className={`text-xs font-bold uppercase tracking-wider py-2 rounded-sm transition-colors ${
                manualMode === "bulk" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
              }`}
              data-testid="manual-mode-bulk"
            >
              Periode
            </button>
            <button
              onClick={() => setManualMode("rooster")}
              className={`text-xs font-bold uppercase tracking-wider py-2 rounded-sm transition-colors ${
                manualMode === "rooster" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
              }`}
              data-testid="manual-mode-rooster"
            >
              Maandrooster
            </button>
          </div>

          {manualMode === "dag" && (
            <div className="grid gap-4 py-2">
              <div>
                <Label className="text-xs font-semibold">Medewerker</Label>
                <Select value={form.medewerker_id} onValueChange={(v) => setForm({ ...form, medewerker_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="manual-select-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    {visibleMedewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Filiaal</Label>
                <Select value={form.filiaal_id} onValueChange={(v) => setForm({ ...form, filiaal_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="manual-select-filiaal"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Datum</Label>
                  <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} className="rounded-sm mt-1" data-testid="manual-datum" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Uren</Label>
                  <Input type="number" step="0.25" min="0" max="24" value={form.uren} onChange={(e) => setForm({ ...form, uren: e.target.value })} placeholder="8" className="rounded-sm mt-1" data-testid="manual-uren" />
                </div>
              </div>
            </div>
          )}
          {manualMode === "bulk" && (
            <div className="grid gap-4 py-2" data-testid="manual-bulk-form">
              <div>
                <Label className="text-xs font-semibold">Medewerker</Label>
                <Select value={bulkForm.medewerker_id} onValueChange={(v) => setBulkForm({ ...bulkForm, medewerker_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-select-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    {visibleMedewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Filiaal</Label>
                <Select value={bulkForm.filiaal_id} onValueChange={(v) => setBulkForm({ ...bulkForm, filiaal_id: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-select-filiaal"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>
                    {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Datum van</Label>
                  <Input type="date" value={bulkForm.datum_van} onChange={(e) => setBulkForm({ ...bulkForm, datum_van: e.target.value })} className="rounded-sm mt-1" data-testid="bulk-datum-van" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Datum tot</Label>
                  <Input type="date" value={bulkForm.datum_tot} onChange={(e) => setBulkForm({ ...bulkForm, datum_tot: e.target.value })} className="rounded-sm mt-1" data-testid="bulk-datum-tot" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Werkdagen</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="bulk-weekdagen">
                  {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((naam, idx) => {
                    const active = bulkForm.weekdagen.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleWeekdag(idx)}
                        className={`flex-1 min-w-[38px] py-2 text-xs font-bold rounded-sm border transition-colors ${
                          active
                            ? "bg-[#002FA7] text-white border-[#002FA7]"
                            : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"
                        }`}
                        data-testid={`bulk-weekdag-${idx}`}
                      >
                        {naam}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Enkel geselecteerde weekdagen worden ingevuld. Standaard Ma-Vr.
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold">Uren per dag</Label>
                <Input
                  type="number" step="0.25" min="0" max="24"
                  value={bulkForm.uren}
                  onChange={(e) => setBulkForm({ ...bulkForm, uren: e.target.value })}
                  placeholder="8"
                  className="rounded-sm mt-1"
                  data-testid="bulk-uren"
                />
              </div>
              <div className="flex items-center gap-2 bg-[#F4F5F7] border border-border rounded-sm p-2.5">
                <input
                  id="bulk-overschrijven"
                  type="checkbox"
                  checked={bulkForm.overschrijven}
                  onChange={(e) => setBulkForm({ ...bulkForm, overschrijven: e.target.checked })}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="bulk-overschrijven"
                />
                <label htmlFor="bulk-overschrijven" className="text-xs cursor-pointer">
                  Bestaande dagen overschrijven (anders overslaan)
                </label>
              </div>
            </div>
          )}

          {/* ═══ Maandrooster Tab ═══ */}
          {manualMode === "rooster" && (
            <div className="py-2" data-testid="rooster-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs font-semibold">Medewerker</Label>
                  <Select value={roosterMedewerker} onValueChange={(v) => { setRoosterMedewerker(v); setRoosterDagen({}); }}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rooster-medewerker"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                    <SelectContent>
                      {visibleMedewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Maand</Label>
                  <Select value={roosterMaand} onValueChange={setRoosterMaand}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rooster-maand"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MAAND_NAMEN_DROP.map((n, i) => (<SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Jaar</Label>
                  <Select value={roosterJaar} onValueChange={setRoosterJaar}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rooster-jaar"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-[#F4F5F7] border border-border rounded-sm">
                <Button size="sm" variant="outline" onClick={laadRooster} data-testid="rooster-laad-btn">
                  Laad bestaande uren
                </Button>
                <div className="text-[11px] text-muted-foreground flex-1">
                  Tip: gebruik 'Vul Ma-Vr in' om het hele rooster snel te vullen.
                </div>
                <Select
                  onValueChange={(filiaalId) => {
                    if (!filiaalId) return;
                    const u = window.prompt("Hoeveel uren per werkdag?", "8");
                    if (u && parseFloat(u) > 0) vulAllesIn(parseFloat(u), filiaalId, true);
                  }}
                >
                  <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="rooster-vul-werkdagen">
                    <SelectValue placeholder="Vul Ma-Vr in..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filialen.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Grid */}
              <div className="overflow-x-auto border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground w-14">Dag</th>
                      {Array.from({ length: ROOSTER_SLOTS }).map((_, i) => (
                        <th key={i} className="px-2 py-2 text-left font-bold uppercase tracking-wider text-muted-foreground">
                          Slot {i + 1}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-wider text-muted-foreground w-16">Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDagenVanMaand(roosterJaar, roosterMaand).map(({ datum, dag, weekdag }) => {
                      const slots = roosterDagen[datum] || [];
                      const totaal = slots.reduce((s, x) => s + (parseFloat(x.uren) || 0), 0);
                      const weekendClass = weekdag >= 5 ? "bg-[#FFF8F0]" : "";
                      return (
                        <tr
                          key={datum}
                          className={`border-t border-border ${weekendClass}`}
                          data-testid={`rooster-row-${datum}`}
                        >
                          <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                            <span className={`font-bold ${weekdag >= 5 ? "text-[#E53E3E]" : ""}`}>{dag}</span>{" "}
                            <span className="text-muted-foreground">{DAGNAMEN_KORT[weekdag]}</span>
                          </td>
                          {Array.from({ length: ROOSTER_SLOTS }).map((_, slotIdx) => {
                            const slot = slots[slotIdx] || { filiaal_id: "", uren: "" };
                            const filled = slot.filiaal_id && slot.uren;
                            return (
                              <td key={slotIdx} className="px-1.5 py-1.5">
                                <div className="flex gap-1">
                                  <Select
                                    value={slot.filiaal_id}
                                    onValueChange={(v) => setRoosterSlot(datum, slotIdx, "filiaal_id", v)}
                                  >
                                    <SelectTrigger
                                      className={`rounded-sm h-8 text-xs flex-1 min-w-0 ${filled ? "border-[#002FA7]/40 bg-[#EAF1FF]" : ""}`}
                                      data-testid={`rooster-${datum}-slot-${slotIdx}-filiaal`}
                                    >
                                      <SelectValue placeholder="—" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {filialen.map((f) => (
                                        <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    type="number" step="0.25" min="0" max="24"
                                    value={slot.uren}
                                    onChange={(e) => setRoosterSlot(datum, slotIdx, "uren", e.target.value)}
                                    placeholder="u"
                                    className={`rounded-sm h-8 w-14 text-xs tabular-nums ${filled ? "bg-[#F0FAEC] border-[#1F7A1F]/40" : ""}`}
                                    data-testid={`rooster-${datum}-slot-${slotIdx}-uren`}
                                  />
                                  {slot.filiaal_id && (
                                    <button
                                      type="button"
                                      onClick={() => removeRoosterSlot(datum, slotIdx)}
                                      className="w-7 h-8 flex items-center justify-center text-muted-foreground hover:text-[#E53E3E]"
                                      title="Wissen"
                                      data-testid={`rooster-${datum}-slot-${slotIdx}-wis`}
                                    >
                                      <Trash size={12} weight="bold" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-right font-bold tabular-nums">
                            {totaal > 0 ? `${totaal.toFixed(2).replace(".", ",")} u` : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-[#EAF1FF] border-t-2 border-[#002FA7]">
                    <tr>
                      <td className="px-2 py-2 font-bold uppercase tracking-wider text-[#002FA7]">Totaal maand</td>
                      <td colSpan={ROOSTER_SLOTS}></td>
                      <td className="px-2 py-2 text-right font-black tabular-nums text-[#002FA7]" data-testid="rooster-totaal">
                        {Object.values(roosterDagen)
                          .flat()
                          .reduce((s, x) => s + (parseFloat(x.uren) || 0), 0)
                          .toFixed(2)
                          .replace(".", ",")} u
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Opslaan overschrijft bestaande records voor dezelfde dag+filiaal, en voegt nieuwe toe.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Annuleren</Button>
            {manualMode === "dag" && (
              <Button onClick={handleManualSave} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-manual-btn">Toevoegen</Button>
            )}
            {manualMode === "bulk" && (
              <Button onClick={handleBulkSave} disabled={bulkSaving} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-bulk-btn">
                {bulkSaving ? "Bezig..." : "Bulk toevoegen"}
              </Button>
            )}
            {manualMode === "rooster" && (
              <Button onClick={handleRoosterSave} disabled={roosterSaving} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-rooster-btn">
                {roosterSaving ? "Opslaan..." : "Maand opslaan"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-sm rounded-sm" data-testid="edit-record-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Tijden & uren corrigeren</DialogTitle>
            <DialogDescription>{editRecord?.medewerker_naam} — {editRecord?.datum}</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Ingeklokt om</Label>
                <Input
                  type="time"
                  value={editIn}
                  onChange={(e) => setEditIn(e.target.value)}
                  className="rounded-sm mt-1"
                  data-testid="edit-inklok-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Uitgeklokt om</Label>
                <Input
                  type="time"
                  value={editUit}
                  onChange={(e) => setEditUit(e.target.value)}
                  className="rounded-sm mt-1"
                  data-testid="edit-uitklok-input"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Vergeten in- of uitklokken? Vul de juiste tijden in — de uren worden automatisch herberekend
              (volgens het startuur uit Instellingen). Laat een veld leeg om het te wissen.
            </p>
            <div>
              <Label className="text-xs font-semibold">
                Uren {urenHandmatig ? "(handmatig)" : "(wordt herberekend)"}
              </Label>
              <Input
                type="number"
                step="0.25"
                min="0"
                max="24"
                value={editUren}
                onChange={(e) => { setEditUren(e.target.value); setUrenHandmatig(true); }}
                className="rounded-sm mt-1"
                data-testid="edit-uren-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} className="rounded-sm">Annuleren</Button>
            <Button onClick={handleEditSave} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-edit-btn">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
